/**
 * File Upload Utilities
 *
 * Handles file upload operations for different file types including PDFs.
 */

export interface UploadResult {
  docId: string;
  documentName: string;
  processingStats?: {
    textLength: number;
    imageCount: number;
    processingTime: number;
  };
  pdfGcsPath?: string;
  ocrGcsPath?: string;
  ocrMetadata?: any;
}

export interface FileUploadCallbacks {
  onMessage: (message: any) => void;
  addPDFDocument: (
    docId: string,
    documentName: string,
    pdfGcsPath: string,
    ocrGcsPath: string,
    ocrMetadata: any
  ) => void;
  addFormDocument: (
    docId: string,
    documentName: string,
    formData: Record<string, any>,
    isMutable: boolean,
    gcsPath?: string,
    formSchema?: string,
    initialDirty?: boolean
  ) => void;
  updateDocumentContent: (
    docId: string,
    htmlContent: string,
    documentName?: string,
    isToolUpdate?: boolean
  ) => void;
  onDocumentAdded?: (docId: string, documentName: string) => void;
}

/**
 * Normalize a filename for display: strip extension and trim whitespace.
 */
const sanitizeFileDisplayName = (fileName: string): string => {
  if (!fileName) return fileName;
  // Remove the last extension (e.g., .pdf, .docx)
  const withoutExt = fileName.replace(/\.[^.]+$/i, "");
  return withoutExt.trim();
};

/**
 * Get document display name from fileType ID
 */
export const getDocumentNameFromFileType = (
  fileType: string,
  workproductType: string,
  workproductFileConfigs: any
): string => {
  const config = workproductFileConfigs[workproductType];
  if (!config) return fileType;

  const fileTypeConfig = config.allowedFileTypes.find(
    (ft: any) => ft.id === fileType
  );
  return fileTypeConfig?.name || fileType;
};

/**
 * Upload PDF files using the specialized PDF upload endpoint
 */
export const uploadPDFFiles = async (
  files: File[],
  fileType: string,
  workproductId: string,
  workproductType: string,
  backendUrl: string,
  workproductFileConfigs: any,
  callbacks: FileUploadCallbacks,
  chatId?: string
): Promise<UploadResult[]> => {
  const uploadResults: UploadResult[] = [];

  for (const file of files) {
    if (file.type === "application/pdf") {
      const categoryName = getDocumentNameFromFileType(
        fileType,
        workproductType,
        workproductFileConfigs
      );
      const documentName = sanitizeFileDisplayName(file.name) || categoryName;

      // Create FormData for single PDF upload
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentName", documentName);
      formData.append("workproductId", workproductId);
      if (chatId) {
        formData.append("chatId", chatId);
      }

      const response = await fetch(
        `${backendUrl}/api/document-agent/upload-pdf`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      uploadResults.push(result);

      // Add PDF document to context so it appears in document tabs
      callbacks.addPDFDocument(
        result.docId,
        result.documentName,
        result.pdfGcsPath,
        result.ocrGcsPath,
        result.ocrMetadata
      );

      // Notify UI to activate newly added document
      if (callbacks.onDocumentAdded) {
        try {
          callbacks.onDocumentAdded(result.docId, result.documentName);
        } catch {}
      }
    }
  }

  return uploadResults;
};

/**
 * Upload non-PDF files using the regular upload endpoint
 */
export const uploadRegularFiles = async (
  files: File[],
  fileType: string,
  workproductId: string,
  backendUrl: string,
  callbacks: FileUploadCallbacks,
  chatId?: string
): Promise<UploadResult[]> => {
  const uploadResults: UploadResult[] = [];

  for (const file of files) {
    // Create FormData for non-PDF file
    const formData = new FormData();
    formData.append("files", file);
    formData.append("workproductId", workproductId);
    formData.append("fileType", fileType);
    if (chatId) {
      formData.append("chatId", chatId);
    }

    const response = await fetch(
      `${backendUrl}/api/document-agent/upload-files`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 409) {
        throw new Error(`Document already exists: ${errorData.error}`);
      } else {
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }
    }

    const result = await response.json();

    // Add to upload results for success message
    uploadResults.push({
      docId: result.uploadedFiles[0]?.docId || "unknown",
      documentName: result.uploadedFiles[0]?.documentName || file.name,
      processingStats: {
        textLength: 0,
        imageCount: 0,
        processingTime: 0,
      },
    });

    // Optimistically add document(s) to context so they appear immediately
    const uploadedFiles = result.uploadedFiles || [];
    for (const u of uploadedFiles) {
      const uploadedDocId = u.docId;
      const uploadedName = u.documentName;
      const gcsPath = u.fileName; // server returns fileName

      try {
        callbacks.addFormDocument(
          uploadedDocId,
          uploadedName,
          {},
          false,
          gcsPath
        );
      } catch (e) {
      }

      // Notify UI to activate newly added document
      if (callbacks.onDocumentAdded) {
        try {
          callbacks.onDocumentAdded(uploadedDocId, uploadedName);
        } catch {}
      }
    }
  }

  return uploadResults;
};

/**
 * Create success message for file uploads
 */
export const createUploadSuccessMessage = (
  files: File[],
  uploadResults: UploadResult[],
  fileType: string,
  workproductType: string,
  workproductFileConfigs: any,
  language: string = "en"
): string => {
  const categoryName = getDocumentNameFromFileType(
    fileType,
    workproductType,
    workproductFileConfigs
  );
  const pdfFiles = files.filter(f => f.type === "application/pdf");
  const otherFiles = files.filter(f => f.type !== "application/pdf");

  let content = `✅ Successfully uploaded and processed ${uploadResults.length} file(s) as "${categoryName}":`;
  const parts: string[] = [];

  if (pdfFiles.length > 0) {
    const pdfNames = pdfFiles.map(f => f.name).join(", ");
    const totalChars = uploadResults
      .filter(
        r => r.processingStats?.textLength && r.processingStats.textLength > 0
      )
      .reduce((sum, r) => sum + (r.processingStats?.textLength || 0), 0);
    parts.push(
      `${pdfFiles.length} PDF(s) [${pdfNames}] with OCR processing (${totalChars} characters extracted)`
    );
  }

  if (otherFiles.length > 0) {
    const otherNames = otherFiles.map(f => f.name).join(", ");
    parts.push(`${otherFiles.length} other file(s) [${otherNames}]`);
  }

  content += " " + parts.join(" and ");
  content += ".";

  return content;
};

/**
 * Get the follow-up prompt for file uploads based on language
 */
export const getUploadFollowUpPrompt = (language: string = "en"): string => {
  return language === "zh"
    ? "**您希望我如何处理这份文档？**"
    : "**What would you like me to do with this document?**";
};

/**
 * Main file upload handler
 */
export const handleFileUpload = async (
  files: File[],
  fileType: string,
  workproductId: string,
  workproductType: string,
  backendUrl: string,
  workproductFileConfigs: any,
  callbacks: FileUploadCallbacks,
  chatId?: string,
  language: string = "en"
): Promise<void> => {

  // Separate files by type
  const pdfFiles = files.filter(f => f.type === "application/pdf");
  const otherFiles = files.filter(f => f.type !== "application/pdf");

  const allResults: UploadResult[] = [];

  // Upload PDF files
  if (pdfFiles.length > 0) {
    const pdfResults = await uploadPDFFiles(
      pdfFiles,
      fileType,
      workproductId,
      workproductType,
      backendUrl,
      workproductFileConfigs,
      callbacks,
      chatId
    );
    allResults.push(...pdfResults);
  }

  // Upload other files
  if (otherFiles.length > 0) {
    const otherResults = await uploadRegularFiles(
      otherFiles,
      fileType,
      workproductId,
      backendUrl,
      callbacks,
      chatId
    );
    allResults.push(...otherResults);
  }

  // Create success message
  const content = createUploadSuccessMessage(
    files,
    allResults,
    fileType,
    workproductType,
    workproductFileConfigs,
    language
  );

  const uploadMessage = {
    id: Date.now().toString(),
    role: "system",
    content,
    timestamp: new Date(),
  };

  callbacks.onMessage(uploadMessage);

  // Send follow-up prompt as a separate assistant message
  const followUpMessage = {
    id: (Date.now() + 1).toString(),
    role: "assistant",
    content: getUploadFollowUpPrompt(language),
    timestamp: new Date(),
  };

  callbacks.onMessage(followUpMessage);
};
